import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { US_STATES, AGE_GROUPS } from "@alpha/shared";
import {
  emptySelfServeDraft,
  canAdvance,
  dateError,
  selfServePayload,
  withState,
  type SelfServeStep,
  type SelfServeDraft,
} from "../lib/selfServe";
import { US_TIMEZONES, timezoneLabel } from "../lib/timezones";
import { api, apiErrorMessage } from "../lib/api";
import { SESSION_KEY } from "../lib/me";
import { useAuth } from "../lib/auth";
import { formatDateRange } from "../lib/format";
import type { Session } from "../lib/types";
import {
  PageHeader,
  Card,
  Field,
  Input,
  Button,
  Select,
  SignInCard,
} from "../components/ui";

export function HostNew() {
  const { idToken, ready } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<SelfServeStep>(1);
  const [draft, setDraft] = useState<SelfServeDraft>(emptySelfServeDraft());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!ready) return null;
  if (!idToken) {
    return (
      <SignInCard
        title="Create a tournament"
        hint="Sign in, or make an account, and you will come back to this form."
        next="/host/new"
      />
    );
  }

  async function submit() {
    setError(null);
    setPending(true);
    try {
      const res = await api.post<{ id: string }>("/me/tournaments", selfServePayload(draft));
      // The console gate reads this session. Until the refetch below lands it
      // holds no grant on the new event, and the first render would refuse it.
      const session = queryClient.getQueryData<Session>(SESSION_KEY);
      if (session) {
        queryClient.setQueryData<Session>(SESSION_KEY, {
          ...session,
          tournamentRoles: [
            ...(session.tournamentRoles ?? []),
            { tournamentId: res.id, role: "tournamentAdmin" },
          ],
        });
      }
      queryClient.invalidateQueries({ queryKey: SESSION_KEY });
      queryClient.invalidateQueries({ queryKey: ["admin-tournaments"] });
      navigate(`/admin/t/${res.id}`);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not create the tournament. Try again."));
      setPending(false);
    }
  }

  function advance() {
    if (step < 4) setStep((step + 1) as SelfServeStep);
    else submit();
  }

  const dErr = dateError(draft);
  const showSummary = (s: SelfServeStep) => step !== s && canAdvance(draft, s);

  return (
    <div className="mx-auto max-w-md space-y-8">
      <PageHeader title="Create a tournament" />
      <Card className="p-6">
        <div className="space-y-6">
          {showSummary(1) && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{draft.name.trim()}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep(1)}>Edit</Button>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4">
              <Field label="Name">
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Summer Slam 2026"
                />
              </Field>
            </div>
          )}

          {showSummary(2) && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">{formatDateRange(draft.startDate, draft.endDate)}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>Edit</Button>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4">
              <Field label="Start date">
                <Input
                  type="date"
                  value={draft.startDate}
                  onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
                />
              </Field>
              <Field label="End date">
                <Input
                  type="date"
                  value={draft.endDate}
                  onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                />
              </Field>
              {dErr && <p className="text-sm text-red-600">{dErr}</p>}
            </div>
          )}

          {showSummary(3) && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-700">
                {draft.location.trim()}, {draft.state} · {timezoneLabel(draft.timezone)}
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setStep(3)}>Edit</Button>
            </div>
          )}
          {step === 3 && <PlaceStep draft={draft} onChange={setDraft} />}

          {step === 4 && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map((g) => {
                  const selected = draft.ageGroups.includes(g);
                  return (
                    <Button
                      key={g}
                      type="button"
                      variant={selected ? "primary" : "secondary"}
                      aria-pressed={selected}
                      onClick={() => {
                        const next = selected
                          ? draft.ageGroups.filter((x) => x !== g)
                          : [...draft.ageGroups, g];
                        setDraft({ ...draft, ageGroups: next });
                      }}
                    >
                      {g}
                    </Button>
                  );
                })}
              </div>
              <details
                open={draft.rulesOpen}
                onToggle={(e) => setDraft({ ...draft, rulesOpen: e.currentTarget.open })}
                className="group rounded-lg border border-slate-200"
              >
                <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700">
                  Adjust rules
                </summary>
                <div className="space-y-4 border-t border-slate-200 p-4">
                  <Field label="Game length">
                    <Input
                      type="number"
                      value={draft.config.gameLengthMinutes}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          config: { ...draft.config, gameLengthMinutes: e.target.valueAsNumber },
                        })
                      }
                    />
                  </Field>
                  <Field label="Run rule">
                    <Input
                      type="number"
                      value={draft.config.runCapMargin}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          config: { ...draft.config, runCapMargin: e.target.valueAsNumber },
                        })
                      }
                    />
                  </Field>
                  <Field label="Forfeit score">
                    <Input
                      type="number"
                      value={draft.config.forfeitScore.winner}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          config: {
                            ...draft.config,
                            forfeitScore: { ...draft.config.forfeitScore, winner: e.target.valueAsNumber },
                          },
                        })
                      }
                    />
                  </Field>
                  <Field label="Rest between games">
                    <Input
                      type="number"
                      value={draft.config.minRestMinutes}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          config: { ...draft.config, minRestMinutes: e.target.valueAsNumber },
                        })
                      }
                    />
                  </Field>
                </div>
              </details>
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          )}

          <div className="pt-4">
            <Button
              type="button"
              className="w-full"
              disabled={!canAdvance(draft, step) || pending}
              onClick={advance}
            >
              {step === 4 ? "Create tournament" : "Next"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * Step 3: where the event is, and so the zone its game times are in. Its own
 * component so a static render can reach it; the wizard paints step 1 first.
 */
export function PlaceStep({
  draft,
  onChange,
}: {
  draft: SelfServeDraft;
  onChange: (draft: SelfServeDraft) => void;
}) {
  return (
    <div className="space-y-4">
      <Field label="City">
        <Input
          value={draft.location}
          onChange={(e) => onChange({ ...draft, location: e.target.value })}
        />
      </Field>
      <Field label="State">
        <Select value={draft.state} onChange={(e) => onChange(withState(draft, e.target.value))}>
          <option value="">Select a state</option>
          {US_STATES.map((s) => (
            <option key={s.code} value={s.code}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field
        label="Timezone"
        hint="Set from the state. Every game time is entered and shown in this zone."
      >
        <Select
          value={draft.timezone}
          disabled={!draft.state}
          onChange={(e) => onChange({ ...draft, timezone: e.target.value })}
        >
          {!draft.timezone && <option value="">Select a state first</option>}
          {US_TIMEZONES.map((z) => (
            <option key={z.zone} value={z.zone}>
              {z.label}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}
